import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { BonusesDataState, UserBonusInfo } from '../models';
import { setSelectedPayGroupId } from './global';

export const initialState: BonusesDataState = {
  userBonuses: [],
  isBonusPresent: false,
  isGeneralBonus: false,
  userBonusesByPayGroups: null,
  selectedBonus: undefined,
  isManuallySelectedBonus: false,
  isAllowBonusSelection: false,
  isAllBonusesNotSelected: false,
};

const WITHOUT_BONUS: UserBonusInfo = {
  bonusDescription: '',
  bonusName: '',
  info_line: '',
  maxDepositAmount: 0,
  minDepositAmount: 0,
  withoutBonus: true,
};

const resetBonusState = (state: BonusesDataState) => {
  state.userBonuses = [];
  state.isBonusPresent = false;
  state.isGeneralBonus = false;
  state.userBonusesByPayGroups = null;
  state.selectedBonus = undefined;
  state.isManuallySelectedBonus = false;
};

const tryPreserveSelectedBonus = (state: BonusesDataState, payload: UserBonusInfo[]): boolean => {
  if (!state.isManuallySelectedBonus || !state.selectedBonus || !state.isAllowBonusSelection) {
    return false;
  }

  // If user selected "without bonus", always preserve it
  if (state.selectedBonus.withoutBonus) {
    return true;
  }

  // Try to find the same bonus by promotion_id
  const cached = payload.find((b) => b.promotion_id === state.selectedBonus?.promotion_id);
  if (cached) {
    state.selectedBonus = cached;
    return true;
  }

  // If bonus not found in the new list (e.g. currency/tab switch),
  // don't preserve — let the main logic pick the backend-selected bonus
  state.isManuallySelectedBonus = false;
  return false;
};

const bonusSlice = createSlice({
  name: 'bonus',
  initialState,
  reducers: {
    setBonusesInfo(state, action: PayloadAction<UserBonusInfo[]>) {
      const { payload } = action;
      if (!payload?.length) {
        resetBonusState(state);
        return;
      }

      const generalBonus = payload.find(
        (b) => (!b.bonusPayGroupsIds || b.bonusPayGroupsIds.length === 0) && (!b.payGroups || b.payGroups.length === 0),
      );
      const allPayGroupIds = generalBonus ? [] : payload.flatMap((b) => b.bonusPayGroupsIds || []);
      const isGeneralBonus = Boolean(generalBonus);

      state.userBonuses = payload;
      state.isBonusPresent = true;
      state.userBonusesByPayGroups = allPayGroupIds.length ? allPayGroupIds : null;
      state.isGeneralBonus = isGeneralBonus;

      // Reset selectedBonus before re-selecting, unless manually set
      if (!state.isManuallySelectedBonus) {
        state.selectedBonus = undefined;
      }

      if (tryPreserveSelectedBonus(state, payload)) {
        return;
      }

      if (!state.isAllowBonusSelection) {
        [state.selectedBonus] = payload;
        state.isManuallySelectedBonus = false;
        return;
      }

      const backendSelected = payload.find((b) => b.is_selected);
      state.selectedBonus = backendSelected || WITHOUT_BONUS;
      state.isManuallySelectedBonus = false;
      state.isAllBonusesNotSelected = !backendSelected;
    },
    setSelectedBonus(state, action: PayloadAction<UserBonusInfo>) {
      state.selectedBonus = action.payload;
      state.isManuallySelectedBonus = true;
    },
    // Not a manual selection — only for GB experiment
    setSelectedBonusByExperiment(state, action: PayloadAction<UserBonusInfo>) {
      state.selectedBonus = action.payload;
    },
    resetSelectedBonusToWithout(state) {
      state.selectedBonus = WITHOUT_BONUS;
      // Not a manual selection — don't set isManuallySelectedBonus
    },
    clearBonuses(state) {
      // Only clear list data, keep selectedBonus and isManuallySelectedBonus
      state.userBonuses = [];
      state.isBonusPresent = false;
      state.isGeneralBonus = false;
      state.userBonusesByPayGroups = null;
    },
    clearSelectedBonus(state) {
      state.selectedBonus = undefined;
      state.isManuallySelectedBonus = false;
    },
    setIsAllowBonusSelection(state, action: PayloadAction<boolean>) {
      state.isAllowBonusSelection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSelectedPayGroupId, (state, action) => {
      if (!state.selectedBonus || state.selectedBonus.withoutBonus) return;
      const isSelectedBonusGeneral = !state.selectedBonus.payGroups || state.selectedBonus.payGroups.length === 0;
      if (isSelectedBonusGeneral) return;
      if (state.selectedBonus.bonusPayGroupsIds?.includes(action.payload)) return;
      state.selectedBonus = WITHOUT_BONUS;
    });
  },
});

export const bonusDataSelector = createSelector(
  (state: { bonus: BonusesDataState }) => state.bonus,
  (bonus) =>
    bonus?.isBonusPresent
      ? {
          bonusInfo: bonus.selectedBonus,
          bonusesList: bonus.userBonuses,
          infoLine: bonus.infoLine,
          bonusByPayGroup: bonus.userBonusesByPayGroups,
          isGeneralBonus: bonus.isGeneralBonus,
        }
      : null,
);

export const selectedBonusSelector = createSelector(
  (state: { bonus: BonusesDataState }) => state.bonus.selectedBonus,
  (selectedBonus) => selectedBonus,
);

export const isAllowBonusSelectionSelector = (state: { bonus: BonusesDataState }) => state.bonus.isAllowBonusSelection;

export const firstBonusSelector = (state: { bonus: BonusesDataState }) => state.bonus.userBonuses[0] ?? null;
export const isAllBonusesNotSelectedSelector = (state: { bonus: BonusesDataState }) =>
  state.bonus.isAllBonusesNotSelected;

export const {
  setBonusesInfo,
  setSelectedBonus,
  setSelectedBonusByExperiment,
  resetSelectedBonusToWithout,
  clearBonuses,
  clearSelectedBonus,
  setIsAllowBonusSelection,
} = bonusSlice.actions;

export default bonusSlice.reducer;
